export interface TagOption {
  id: string
  value: string
  label: string
  /** When this option applies — guides both humans and the AI tag suggester. */
  description?: string
  color: string
  isSystem: boolean
  sortOrder: number
  productId?: string
}

export interface TagCategory {
  id: string
  slug: string
  label: string
  description?: string
  isSystem: boolean
  allowMultiple: boolean
  valueType: "predefined" | "user_generated" | "mixed" | "free_text"
  sortOrder: number
  options: TagOption[]
}

export interface TagAssignment {
  id: string
  categoryId: string
  categorySlug: string
  optionId: string
  optionValue: string
  optionLabel: string
  optionColor: string
  source: "manual" | "ai_suggested" | "ai_confirmed" | "bulk_import"
  confidence?: number
  createdAt: string
}

/** Map of category slug -> array of option IDs */
export type ItemTagState = Record<string, string[]>

/**
 * Characters reserved as ad-name separators (the naming-convention presets
 * `_ - | /` plus the `{ }` used by `{slug}` templates). A free-text tag value
 * containing one of these would corrupt parsing an ad name back into tags, so
 * inputs for `free_text` categories strip them as the user types. Space is NOT
 * reserved — names split on the company's configured separator, not whitespace.
 *
 * Mirrors `sanitizeFreeTextLabel` in
 * `apps/api/src/services/tags/tag-categories.service.ts` — keep in sync.
 */
export const TAG_RESERVED_SEPARATOR_CHARS = /[_\-|/\\{}]/g

/** Human-readable list of the reserved characters, for input hints. */
export const TAG_RESERVED_SEPARATOR_HINT = "_ - | / \\ { }"

/**
 * Sentinel id prefix for a free-text value that has been typed but not saved
 * yet. Pending ids live only in local selection state (`ItemTagState`); on
 * save the label is sent as `freeTextTags` and the server resolves it to a
 * real option id in the same request. Nothing is persisted until Save.
 */
export const PENDING_FREE_TEXT_PREFIX = "text:"

export function pendingFreeTextId(label: string): string {
  return `${PENDING_FREE_TEXT_PREFIX}${label}`
}

export function isPendingFreeTextId(id: string): boolean {
  return id.startsWith(PENDING_FREE_TEXT_PREFIX)
}

export function pendingFreeTextLabel(id: string): string {
  return id.slice(PENDING_FREE_TEXT_PREFIX.length)
}

/** Strip reserved separator characters and collapse whitespace. */
export function sanitizeFreeTextTagValue(text: string): string {
  return text
    .replace(TAG_RESERVED_SEPARATOR_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * As-you-type variant: strips reserved characters but preserves interior
 * spacing and a trailing space so the user can keep typing multi-word values.
 */
export function filterFreeTextTagInput(text: string): string {
  return text.replace(TAG_RESERVED_SEPARATOR_CHARS, "").replace(/\s{2,}/g, " ")
}

/**
 * Render-time formatter for category/option labels: replaces underscores with
 * spaces and Title-Cases each word. Idempotent for already-pretty labels.
 *
 * - "creative_name"  -> "Creative Name"
 * - "high_production" -> "High Production"
 * - "Hook"           -> "Hook"
 */
export function formatTagLabel(label: string): string {
  return label
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1))
}
